import React, { useMemo, useState } from "react";
import {
    Box,
    Collapse,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    Tooltip,
    Chip,
    Stack,
    TablePagination,
    CircularProgress,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Section from "../../../../common/components/Dashboard/Section";
import { useDashboardTrackingHistory } from "../../../../common/API/hooks";
import { useSelectorValue } from "../../../customDashboards/common/providers/selectorProvider";
import { formatDateToObjectFromAndTo, groupByDateAndModule } from "../utils";
import moment from "moment";

const PAGE_SIZE = 50;
const DEFAULT_SORT_BY = "datetime";
const DEFAULT_SORT_ORDER = "desc";

function getDashboardName(module) {
    const dashboardRegistry = window.dashboardRegistry;
    if (!dashboardRegistry) return module;
    const [dashboardPath, dashboardDetail] = module.split(":");
    if (dashboardRegistry[dashboardPath]) {
        return (
            dashboardRegistry[dashboardPath].name +
            (dashboardDetail ? ` — ${dashboardDetail}` : "")
        );
    }
    return module;
}

function VisitRow({ row }) {
    const [open, setOpen] = useState(false);
    const dashboardName = getDashboardName(row.module);

    return (
        <>
            <TableRow
                hover
                sx={{ "& > *": { borderBottom: "unset" }, cursor: "pointer" }}
                onClick={() => setOpen(!open)}
            >
                <TableCell padding="checkbox">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{row.date}</TableCell>
                <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">{dashboardName}</Typography>
                        <Tooltip title={`Open: #/${row.module}`} placement="right">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`#/${row.module}`, "_blank");
                                }}
                            >
                                <OpenInNewIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        {row.module}
                    </Typography>
                </TableCell>
                <TableCell align="center">
                    <Chip label={row.visits.length} size="small" color="primary" variant="outlined" />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, ml: 6 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Visit timestamps
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Timestamp</TableCell>
                                        <TableCell>Detail</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {row.visits
                                        .slice()
                                        .sort((a, b) => b.datetime.localeCompare(a.datetime))
                                        .map((visit, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{idx + 1}</TableCell>
                                                <TableCell>
                                                    {moment(visit.datetime).format("YYYY-MM-DD HH:mm:ss")}
                                                </TableCell>
                                                <TableCell>
                                                    {visit.detail ? (
                                                        <Chip
                                                            label={visit.detail}
                                                            size="small"
                                                            variant="outlined"
                                                            color="default"
                                                            sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
                                                        />
                                                    ) : null}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function DashboardVisitTable({ username }) {
    const date = useSelectorValue("date");
    const dateTo = useSelectorValue("dateTo");
    const module = useSelectorValue("module");
    const dateRange = formatDateToObjectFromAndTo(date, dateTo);

    const [page, setPage] = useState(0);
    const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
    const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(column);
            setSortOrder(column === "datetime" ? "desc" : "asc");
        }
        setPage(0);
    };

    // Reset to first page when filters change
    const filters = useMemo(() => {
        setPage(0);
        return {
            ...dateRange,
            username: username || null,
            module_name: module === "All" ? null : module,
            page: 1,
            page_size: PAGE_SIZE,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(dateRange), username, module]);

    const apiFilters = useMemo(
        () => ({ ...filters, page: page + 1, sort_by: sortBy, sort_order: sortOrder }),
        [filters, page, sortBy, sortOrder]
    );

    const { data, isLoading, error } = useDashboardTrackingHistory(apiFilters);

    const results = data?.results ?? [];
    const groupedRows = useMemo(() => groupByDateAndModule(results), [results]);

    const hasNextPage = results.length === PAGE_SIZE;
    const totalCount = hasNextPage ? -1 : page * PAGE_SIZE + results.length;

    const muiSortOrder = sortOrder === "asc" ? "asc" : "desc";

    const renderContent = () => {
        if (error)
            return (
                <Typography variant="body1" color="error" p={2}>
                    Error loading data.
                </Typography>
            );

        return (
            <Box>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f6f6f8" }}>
                                <TableCell padding="checkbox" />
                                <TableCell sx={{ fontWeight: 700 }}>
                                    <TableSortLabel
                                        active={sortBy === "datetime"}
                                        direction={sortBy === "datetime" ? muiSortOrder : "desc"}
                                        onClick={() => handleSort("datetime")}
                                    >
                                        Date
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    <TableSortLabel
                                        active={sortBy === "module__name"}
                                        direction={sortBy === "module__name" ? muiSortOrder : "asc"}
                                        onClick={() => handleSort("module__name")}
                                    >
                                        Dashboard
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>
                                    Visits
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : groupedRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No records found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                groupedRows.map((row) => <VisitRow key={row.id} row={row} />)
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={PAGE_SIZE}
                    rowsPerPageOptions={[PAGE_SIZE]}
                    slotProps={{
                        actions: {
                            nextButton: { disabled: isLoading || !hasNextPage },
                            previousButton: { disabled: isLoading || page === 0 },
                        },
                    }}
                    labelDisplayedRows={({ from }) =>
                        hasNextPage
                            ? `Page ${page + 1}`
                            : `Page ${page + 1} — ${from + groupedRows.length - 1} total groups`
                    }
                />
            </Box>
        );
    };

    return (
        <Section title={"Dashboard Visit History"} isCollapsible={true}>
            {renderContent()}
        </Section>
    );
}
